import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCodeBlock,
  DocsStep,
  DocsSteps,
} from '@/components/modules/Docs';
import {
  AgentProtocolReference,
  CHATCO_MCP_URL,
  ChatcoDisconnect,
  ConnectorCreditsTable,
  ConnectorErrors,
  ConnectorPrerequisites,
  ConnectorReferences,
  ConnectorStatusPanel,
  ConsentExplainer,
  FirstRunWalkthrough,
  getConnectorHost,
  getConnectorNeighbours,
} from '@/components/modules/Docs/connectors';

const host = getConnectorHost('cursor');

const tableOfContents = [
  { id: 'availability', title: '1. Availability' },
  { id: 'before-you-start', title: '2. Before you start' },
  { id: 'connect', title: '3. Add Chat.co to Cursor' },
  { id: 'approve', title: '4. Approve in Chat.co' },
  { id: 'first-answer', title: '5. Your first cited answer' },
  { id: 'credits', title: '6. Message credits' },
  { id: 'troubleshooting', title: '7. Common errors' },
  { id: 'disconnect', title: '8. Disconnect' },
  { id: 'developer-reference', title: '9. For developers & agents' },
  { id: 'references', title: '10. References' },
];

const CURSOR_MCP_JSON = `{
  "mcpServers": {
    "chatco": {
      "url": "${CHATCO_MCP_URL}"
    }
  }
}`;

const stepLink = 'font-medium text-primary-700 underline';

const CursorConnectorGuide = () => (
  <>
    <NextSeo
      title="Use Chat.co experts in Cursor"
      description="Add the Chat.co MCP server to Cursor from cursor.directory or mcp.json, sign in with OAuth, and get cited answers from your own experts."
      canonical={`https://chat.co${host.href}`}
      openGraph={{
        url: `https://chat.co${host.href}`,
        title: 'Use Chat.co experts in Cursor | Chat.co Docs',
        description: 'Add the Chat.co MCP server to Cursor and get cited answers.',
        type: 'article',
        article: {
          publishedTime: '2026-09-30T00:00:00Z',
          modifiedTime: '2026-09-30T00:00:00Z',
          section: 'Integrations',
          tags: ['cursor', 'mcp.json', 'connector', 'mcp', 'experts', 'citations'],
        },
      }}
    />
    <DocsArticleLayout
      category="Experts in AI apps"
      categoryColor="text-orange-700"
      title="Use your Chat.co experts in Cursor"
      description="Install Chat.co from the community directory or add one entry to mcp.json, then sign in from Cursor’s MCP settings."
      readingTime="7 min read"
      lastUpdated="September 2026"
      tableOfContents={tableOfContents}
      {...getConnectorNeighbours('cursor')}
      keyTakeaways={[
        'A community listing on cursor.directory is live; no official Cursor marketplace decision is confirmed.',
        'The manual setup is a single url entry in mcp.json — no API key or header.',
        'Sign-in and tool discovery are verified in Cursor; the full question flow has not been independently verified there yet.',
        'Keep Cursor’s tool approvals on. Chat.co needs no sandbox or auto-run changes.',
      ]}>
      <section id="availability" className="scroll-mt-28">
        <h2>1. Availability</h2>
        <ConnectorStatusPanel host={host} />
        <p>
          The{' '}
          <a
            href="https://cursor.directory/plugins/chatco-experts"
            target="_blank"
            rel="noopener noreferrer">
            Chat.co listing on cursor.directory
          </a>{' '}
          is a community directory entry, separate from Cursor&apos;s official marketplace. We have
          applied to the official marketplace; Cursor&apos;s decision has not been confirmed.
        </p>
        <p>
          In Cursor we have verified OAuth sign-in and discovery of all four tools. The complete
          flow of asking an expert and opening its sources has not yet been independently verified
          inside Cursor. It uses the same server as the hosts where it has been.
        </p>
      </section>

      <section id="before-you-start" className="scroll-mt-28">
        <h2>2. Before you start</h2>
        <ConnectorPrerequisites>
          <li>
            <strong>Cursor installed and signed in</strong>, with access to Agent chat.
          </li>
        </ConnectorPrerequisites>
      </section>

      <section id="connect" className="scroll-mt-28">
        <h2>3. Add Chat.co to Cursor</h2>
        <h3 id="connect-directory">Option A: from cursor.directory</h3>
        <DocsSteps>
          <DocsStep title="Open the listing">
            <p>
              Go to{' '}
              <a
                href="https://cursor.directory/plugins/chatco-experts"
                target="_blank"
                rel="noopener noreferrer"
                className={stepLink}>
                cursor.directory/plugins/chatco-experts
              </a>{' '}
              and select <strong>Add to Cursor</strong>.
            </p>
          </DocsStep>
          <DocsStep title="Check the server in Cursor">
            <p>
              Cursor opens with the server to install. Check that it points at{' '}
              <code>{CHATCO_MCP_URL}</code> and contains no credentials, then install it.
            </p>
          </DocsStep>
        </DocsSteps>

        <h3 id="connect-manual">Option B: edit mcp.json</h3>
        <p>
          Cursor reads MCP servers from <code>.cursor/mcp.json</code> in a project, or from{' '}
          <code>~/.cursor/mcp.json</code> for every project. Remote servers need only a{' '}
          <code>url</code>. Add a <code>chatco</code> entry inside your existing{' '}
          <code>mcpServers</code> object rather than replacing the file:
        </p>
        <DocsCodeBlock code={CURSOR_MCP_JSON} language="json" filename=".cursor/mcp.json" />
        <DocsCallout variant="warning" title="Leave headers out">
          Do not add an <code>Authorization</code> header, API key or client secret. Cursor handles
          sign-in through OAuth.
        </DocsCallout>

        <h3 id="connect-sign-in">Sign in</h3>
        <DocsSteps>
          <DocsStep title="Open Cursor’s MCP settings">
            <p>
              In current versions this is the <strong>Customize</strong> panel in the sidebar. Find{' '}
              <strong>chatco</strong> in the MCP servers list.
            </p>
          </DocsStep>
          <DocsStep title="Connect">
            <p>
              Select the connect or login action next to chatco. Your browser opens the Chat.co
              approval page. After you approve, Cursor lists four tools:{' '}
              <code>list_authorized_experts</code>, <code>consult_expert</code>, <code>search</code>{' '}
              and <code>fetch</code>.
            </p>
          </DocsStep>
        </DocsSteps>
        <DocsCallout variant="tip" title="Keep approvals on">
          Cursor can ask before it runs a tool. Leave that on. Chat.co doesn&apos;t need auto-run,
          and you don&apos;t need to change any sandbox or security setting.
        </DocsCallout>
      </section>

      <section id="approve" className="scroll-mt-28">
        <h2>4. Approve in Chat.co</h2>
        <ConsentExplainer />
      </section>

      <section id="first-answer" className="scroll-mt-28">
        <h2>5. Your first cited answer</h2>
        <FirstRunWalkthrough where="Cursor’s Agent chat" />
      </section>

      <section id="credits" className="scroll-mt-28">
        <h2>6. Message credits</h2>
        <ConnectorCreditsTable />
      </section>

      <section id="troubleshooting" className="scroll-mt-28">
        <h2>7. Common errors</h2>
        <ConnectorErrors
          hostRows={[
            [
              'chatco shows as needing login',
              'Select its connect or login action in Cursor’s MCP settings and approve in Chat.co.',
            ],
            [
              'chatco shows an error or no tools',
              'Check that mcp.json is valid JSON and the url is exact. Cursor’s Output panel has an “MCP Logs” channel with the details.',
            ],
            [
              'The agent answers without using Chat.co',
              'Make sure chatco is enabled, then name it: “Use my Chat.co experts to…”.',
            ],
          ]}
        />
      </section>

      <section id="disconnect" className="scroll-mt-28">
        <h2>8. Disconnect</h2>
        <ChatcoDisconnect>
          <p>
            In Cursor&apos;s MCP settings, turn chatco off or delete it. Or remove the{' '}
            <code>chatco</code> entry from <code>mcp.json</code>.
          </p>
        </ChatcoDisconnect>
      </section>

      <section id="developer-reference" className="scroll-mt-28">
        <h2>9. For developers &amp; agents</h2>
        <AgentProtocolReference />
      </section>

      <section id="references" className="scroll-mt-28">
        <h2>10. References</h2>
        <ConnectorReferences
          vendorLinks={[
            {
              label: 'Chat.co on cursor.directory (community listing)',
              href: 'https://cursor.directory/plugins/chatco-experts',
            },
            {
              label: 'Cursor: Model Context Protocol (MCP)',
              href: 'https://cursor.com/docs/context/mcp',
            },
            {
              label: 'Chat.co experts package on GitHub',
              href: 'https://github.com/01-Atlas/chatco-experts',
            },
          ]}
        />
      </section>
    </DocsArticleLayout>
  </>
);

export default CursorConnectorGuide;
